/**
 * Wallet API client — F-009.3 (UI wallet, lato cliente).
 *
 * Wrapper `fetch` tipizzati per gli endpoint wallet (F-009 backend):
 *  - GET  /api/v1/wallet               → WalletSummaryResponse
 *  - GET  /api/v1/wallet/transactions  → WalletTransactionsResponse
 *  - POST /api/v1/wallet/ricarica      → RicaricaResponse (URL Stripe Checkout)
 *
 * Convenzioni (mirror di `features/admin-wallet/lib/admin-client.ts`):
 *  - `credentials: 'include'` → il cookie di sessione viaggia con ogni request.
 *  - Errori → `WalletApiError` con `status` + `code`; `errorMessageFor(code)`
 *    mappa i codici noti su copy italiano per i toast.
 *
 * Wire format: i campi monetari BigInt sono serializzati dall'API come stringhe
 * decimali, le date come ISO. I tipi di response arrivano da `@app/shared`
 * (già modellati con cents = string).
 */
import type {
  WalletSummaryResponse,
  WalletTransactionsResponse,
  RicaricaResponse,
  WalletReason,
  ServizioResponse,
  ServizioListResponse,
} from '@app/shared';

const API = '/api/v1/wallet';
const SERVIZI_API = '/api/v1/servizi';

/* -------------------------------------------------------------------------- */
/* Query                                                                      */
/* -------------------------------------------------------------------------- */

export interface WalletTransactionsQueryInput {
  page?: number;
  limit?: number;
  reason?: WalletReason;
  from?: string; // ISO date
  to?: string; // ISO date
}

/* -------------------------------------------------------------------------- */
/* Error handling                                                             */
/* -------------------------------------------------------------------------- */

export class WalletApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'WalletApiError';
    this.status = status;
    this.code = code;
  }
}

export function isWalletApiError(err: unknown): err is WalletApiError {
  return err instanceof WalletApiError;
}

const ERROR_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: 'Importo non valido. Controlla il valore inserito.',
  RATE_LIMIT_EXCEEDED: 'Troppe richieste di ricarica. Riprova tra qualche minuto.',
  STRIPE_NOT_CONFIGURED: 'Pagamenti temporaneamente non disponibili. Riprova più tardi.',
  STRIPE_CHECKOUT_FAILED: 'Impossibile avviare il pagamento. Riprova.',
  UNAUTHORIZED: "Sessione scaduta. Effettua di nuovo l'accesso.",
  WALLET_ACCOUNT_NOT_FOUND: 'Wallet non trovato.',
};

const NETWORK_ERROR_MESSAGE = 'Errore di connessione. Riprova.';

export function errorMessageFor(err: unknown): string {
  if (isWalletApiError(err)) {
    return ERROR_MESSAGES[err.code] ?? err.message ?? 'Si è verificato un errore.';
  }
  if (err instanceof TypeError) return NETWORK_ERROR_MESSAGE;
  return 'Si è verificato un errore. Riprova.';
}

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

async function throwForError(
  res: Response,
  fallbackCode: string,
  fallbackMsg: string,
): Promise<never> {
  const env = (await res
    .json()
    .catch(() => ({ code: 'UNKNOWN', message: 'Errore sconosciuto' }))) as ApiErrorEnvelope;
  throw new WalletApiError(res.status, env.code ?? fallbackCode, env.message ?? fallbackMsg);
}

/* -------------------------------------------------------------------------- */
/* Endpoint wrappers                                                          */
/* -------------------------------------------------------------------------- */

/** GET /api/v1/wallet — summary saldo/fido/disponibilità. */
export async function getWalletSummary(): Promise<WalletSummaryResponse> {
  const res = await fetch(API, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'SUMMARY_FAILED', 'Impossibile caricare il saldo');
  return (await res.json()) as WalletSummaryResponse;
}

/** GET /api/v1/wallet/transactions — storico paginato/filtrato. */
export async function getWalletTransactions(
  query: WalletTransactionsQueryInput = {},
): Promise<WalletTransactionsResponse> {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.reason) params.set('reason', query.reason);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const qs = params.toString();
  const res = await fetch(`${API}/transactions${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) await throwForError(res, 'TRANSACTIONS_FAILED', 'Impossibile caricare lo storico');
  return (await res.json()) as WalletTransactionsResponse;
}

/**
 * POST /api/v1/wallet/ricarica — crea una Stripe Checkout Session.
 * Ritorna l'URL a cui reindirizzare l'utente per completare il pagamento.
 */
export async function createRicarica(amountCents: number): Promise<RicaricaResponse> {
  const res = await fetch(`${API}/ricarica`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amountCents }),
  });
  if (!res.ok) await throwForError(res, 'RICARICA_FAILED', 'Impossibile avviare la ricarica');
  return (await res.json()) as RicaricaResponse;
}

/* -------------------------------------------------------------------------- */
/* F-009.6 — catalogo servizi extra (acquisto self-service)                   */
/* -------------------------------------------------------------------------- */

const ACQUISTO_ERROR_MESSAGES: Record<string, string> = {
  INSUFFICIENT_FUNDS: 'Saldo insufficiente. Ricarica il wallet per acquistare.',
  WALLET_BLOCKED: 'Il wallet è bloccato. Contatta il supporto.',
  SERVIZIO_NOT_ACTIVE: 'Servizio non più disponibile.',
  SERVIZIO_NOT_FOUND: 'Servizio non trovato.',
};

/** Messaggio per gli errori di acquisto (estende `errorMessageFor`). */
export function acquistoErrorMessageFor(err: unknown): string {
  if (isWalletApiError(err)) {
    return ACQUISTO_ERROR_MESSAGES[err.code] ?? errorMessageFor(err);
  }
  return errorMessageFor(err);
}

/** GET /api/v1/servizi — catalogo servizi attivi. */
export async function getServiziCatalog(): Promise<ServizioListResponse> {
  const res = await fetch(SERVIZI_API, { credentials: 'include' });
  if (!res.ok) await throwForError(res, 'CATALOG_FAILED', 'Impossibile caricare i servizi');
  return (await res.json()) as ServizioListResponse;
}

/** POST /api/v1/servizi/:id/acquista — addebita il wallet del servizio. */
export async function acquistaServizio(
  servizioId: string,
  idempotencyKey: string,
): Promise<{ servizio: ServizioResponse }> {
  const res = await fetch(`${SERVIZI_API}/${encodeURIComponent(servizioId)}/acquista`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idempotencyKey }),
  });
  if (!res.ok) await throwForError(res, 'ACQUISTO_FAILED', "Impossibile completare l'acquisto");
  return (await res.json()) as { servizio: ServizioResponse };
}
